/**
 * AdminTopNav — barra superior de la app interna (variante topbar del diseño).
 *
 * Barra oscura (token `--nav`): logo + navegación gateada por rol (pills) a la
 * izquierda; notificaciones, tema, usuario y logout a la derecha. El contenido
 * va debajo, a todo el ancho. Reemplaza al rail lateral.
 */
import { NavLink } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { LogOut } from 'lucide-react'
import { LeaseMDLogo } from './ui/LeaseMDLogo'
import { useLogout } from '../auth/authProvider'
import { useRole } from '../auth/useRole'
import ThemeToggle from './ui/ThemeToggle'
import NotificacionesBell from './NotificacionesBell'
import BotonReportarProblema from './BotonReportarProblema'

interface NavItemDef {
  to: string
  label: string
  show: boolean
  end?: boolean
}

export default function AdminTopNav() {
  const { accounts } = useMsal()
  const logout = useLogout()
  const { can } = useRole()

  const userName = accounts[0]?.name ?? accounts[0]?.username ?? ''
  const initials =
    userName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'

  const items: NavItemDef[] = [
    { to: '/', label: 'Inicio', show: true, end: true },
    { to: '/oportunidades', label: 'Oportunidades', show: can.view_oportunidades },
    { to: '/revision', label: 'Revisión', show: can.review_cfdi_read },
    { to: '/complementos-pendientes', label: 'Complementos', show: can.view_complementos },
    { to: '/pagos', label: 'Pagos', show: can.view_pagos },
    { to: '/proveedores', label: 'Proveedores', show: can.view_proveedores },
    {
      to: '/admin/config',
      label: 'Configuración',
      show: can.admin_config || can.manage_entidades,
    },
  ]

  // z-40: por encima de las barras sticky de página (z-30) para que el dropdown
  // de notificaciones no quede recortado por ellas, y por debajo de los modales
  // (z-50) y toasts (z-60).
  return (
    <header className="sticky top-0 z-40 flex min-h-14 flex-wrap items-center gap-x-5 gap-y-2 bg-nav px-4 py-2 text-surface-50">
      <LeaseMDLogo variant="inverse" className="h-[18px] w-auto" />

      <nav aria-label="Secciones" className="flex flex-wrap items-center gap-1">
        {items
          .filter((i) => i.show)
          .map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `rounded-lg px-3 py-1.5 text-[13px] font-medium transition ${
                  isActive
                    ? 'bg-nav-active text-white'
                    : 'text-surface-300 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              {label}
            </NavLink>
          ))}
      </nav>

      {/* Chrome del topbar (mockup variante topbar): tema + campana + usuario +
          logout. En esta variante NO viven en el header de página. */}
      <div className="ml-auto flex items-center gap-1.5">
        <BotonReportarProblema variant="onDark" />
        <ThemeToggle variant="onDark" />
        <NotificacionesBell variant="onDark" />
        <div className="mx-1 h-5 w-px bg-white/15" />
        <div
          className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-500 text-[10px] font-bold text-white"
          title={userName}
        >
          {initials}
        </div>
        <button
          onClick={logout}
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
          className="rounded-lg p-1.5 text-surface-300 transition hover:bg-white/10 hover:text-white"
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  )
}
